import React, { useEffect, useState } from 'react';
import { authHeaders } from '../utils/authHeaders';
import '../styles/procurement-admin.css';

const EMPTY = { from: '', to: '', aggregator_id: '', warehouse_id: '', crop_id: '', status: '' };
const STATES = { PREPARED: 'Prepared', DEPARTED: 'Departed', RECEIVED: 'Received', CANCELLED: 'Cancelled' };
const time = value => value ? new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : '—';

export default function RecordedBags() {
  return localStorage.getItem('role') === 'fpo_admin' ? <BagsReport /> : <p role="alert">FPO Admin Recorded Bags Used access required.</p>;
}

function BagsReport() {
  const [filters, setFilters] = useState(EMPTY);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState(null);
  const [options, setOptions] = useState({ aggregators: [], warehouses: [], crops: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  Object.entries(filters).forEach(([key, value]) => { if (value) params.set(key, value); });
  const query = params.toString();
  useEffect(() => {
    let current = true;
    setLoading(true); setError('');
    async function load() {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE}/procurement/v1/admin/reports/recorded-bags?${query}`, { headers: authHeaders() });
        if (!response.ok) {
          let message = 'Unable to load Recorded Bags Used. Please retry.';
          try { const body = await response.json(); if (typeof body.message === 'string') message = body.message; } catch (_) { /* Use fallback for non-JSON errors. */ }
          throw new Error(message);
        }
        const result = await response.json();
        if (current) { setData(result); setOptions(result.filter_options); }
      } catch (err) {
        if (current) setError(err.message === 'Failed to fetch' ? 'Unable to load Recorded Bags Used. Please retry.' : err.message);
      } finally { if (current) setLoading(false); }
    }
    load();
    return () => { current = false; };
  }, [query, refresh]);
  function change(key, value) { setPage(1); setFilters(previous => ({ ...previous, [key]: value })); }
  function select(label, key, list) {
    return <label>{label}<select value={filters[key]} onChange={e => change(key, e.target.value)}><option value="">All</option>{list.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>;
  }
  const totalPages = Math.max(1, Math.ceil((data?.total_rows ?? 0) / pageSize));
  return <section className="proc-admin proc-admin--bags" aria-label="Recorded Bags Used">
    <h3>Recorded Bags Used</h3>
    <p>Optional bag counts recorded during dispatch preparation. Date filters use prepared dates in India. Cancelled dispatches remain visible and are excluded from Recorded Bags Used.</p>
    <div className="bags-filters">
      {['from', 'to'].map(key => <label key={key}>{key === 'from' ? 'From' : 'To'}<input type="date" value={filters[key]} onChange={e => change(key, e.target.value)} /></label>)}
      {select('Aggregator', 'aggregator_id', options.aggregators)}
      {select('Destination Warehouse', 'warehouse_id', options.warehouses)}
      {select('Crop', 'crop_id', options.crops)}
      {select('Status', 'status', Object.entries(STATES).map(([id, name]) => ({ id, name })))}
    </div>
    <button type="button" disabled={loading} onClick={() => setRefresh(value => value + 1)}>Refresh</button>
    {loading && <p role="status">Loading Recorded Bags Used…</p>}
    {error && <p role="alert">{error}</p>}
    {!loading && !error && data && <>
      <div className="bags-kpis">
        {[['Recorded Bags Used', data.summary.recorded_bags_used], ['Dispatches with Bags Recorded', data.summary.dispatches_with_bags_recorded], ['Not Recorded', data.summary.dispatches_not_recorded], ['Cancelled Dispatches', data.summary.cancelled_dispatches]].map(([label, value]) => <div key={label} role="group" aria-label={label}><span>{label}</span><strong>{value}</strong></div>)}
      </div>
      <p>Eligible dispatches: {data.summary.total_eligible_dispatches}. Cancelled bags recorded: {data.summary.cancelled_recorded_bags} (excluded from total). KPIs cover all matching dispatches, across pages.</p>
      <div className="bags-toolbar"><span>Total: {data.total_rows}</span><label>Rows per page<select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>{[50, 100, 200, 500].map(size => <option key={size}>{size}</option>)}</select></label></div>
      <div className="bags-table-scroll" role="region" aria-label="Recorded bags dispatch table" tabIndex={0}>
        <table><thead><tr>{['Prepared Date (India)', 'Dispatch', 'Aggregator', 'Destination Warehouse', 'Crop', 'Variety', 'Field Grade', 'Status', 'Recorded Bags'].map(label => <th key={label} scope="col">{label}</th>)}</tr></thead>
          <tbody>{data.rows.map(row => <tr key={row.dispatch_id} className={row.cancelled ? 'bags-cancelled' : undefined}>
            <td>{time(row.prepared_at)}{row.departure_occurred_at && <small>Departed: {time(row.departure_occurred_at)}</small>}</td><td>{row.dispatch_id}</td><td>{row.aggregator_name}</td><td>{row.warehouse_name}</td><td>{row.crop_name}</td><td>{row.variety_name}</td><td>{row.field_grade_name}</td><td>{STATES[row.status] || row.status}</td><td>{row.bag_count == null ? 'Not recorded' : row.bag_count}{row.cancelled && <small>Excluded from total</small>}</td>
          </tr>)}</tbody></table>
      </div>
      {data.rows.length === 0 && <p role="status">No dispatches match the selected filters.</p>}
      <div className="bags-pagination"><button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button><span>Page {page} of {totalPages}</span><button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button></div>
    </>}
  </section>;
}
